import React, { useState, useEffect, useCallback, useMemo } from "react";
import apiClient from "@/api/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Plus, UserCircle, ChevronUp, ChevronDown, ChevronsUpDown, Mail, MailX, MailCheck, Download } from "lucide-react";
import { formatDateEU } from "@/utils/dateFormat";
import { useAuth } from "@/contexts/AuthContext";

const statusLabels = { ACTIVE: "Actif", SUSPENDED: "Suspendu", EXPIRED: "Inactif", FORMER: "Inactif" };
const statusColors = { ACTIVE: "bg-green-100 text-green-700", SUSPENDED: "bg-blue-100 text-blue-700", EXPIRED: "bg-red-100 text-red-600", FORMER: "bg-red-100 text-red-600" };

const SortIcon = ({ sortKey, currentSort, currentDirection }) => {
  if (currentSort !== sortKey) {
    return <ChevronsUpDown size={14} className="text-[#a1aeba]" />;
  }
  return currentDirection === "asc" 
    ? <ChevronUp size={14} className="text-[#3899ec]" />
    : <ChevronDown size={14} className="text-[#3899ec]" />;
};

const MembersPage = () => {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null); // Start with null to wait for URL param
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ last_name: "", first_name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("last_name");
  const [sortDirection, setSortDirection] = useState("asc");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useAuth();

  // Initialize statusFilter from URL or default to ""
  useEffect(() => {
    const s = searchParams.get("status");
    setStatusFilter(s || "");
  }, [searchParams]);

  const fetchMembers = useCallback(async () => {
    if (statusFilter === null) return; // Wait for initialization
    try {
      const res = await apiClient.get("/members/with-subscriptions", { params: { search, status: statusFilter } });
      setMembers(res.data);
    } catch (e) { 
      const res = await apiClient.get("/members", { params: { search, status: statusFilter } });
      setMembers(res.data);
    }
    setLoading(false);
  }, [search, statusFilter]);

  useEffect(() => { 
    if (statusFilter !== null) fetchMembers(); 
  }, [fetchMembers, statusFilter]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortDirection("asc");
    }
  };

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) => {
      let valA = a[sortBy];
      let valB = b[sortBy];
      
      // Handle dates
      if (sortBy === "subscription_start" || sortBy === "subscription_end") {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      } else if (sortBy === "price_eur") {
        valA = Number(valA) || 0;
        valB = Number(valB) || 0;
      } else if (typeof valA === "string") {
        valA = (valA || "").toLowerCase();
        valB = (valB || "").toLowerCase();
      }
      
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [members, sortBy, sortDirection]);

  const handleAdd = async () => {
    if (!form.last_name || !form.first_name) return;
    try {
      const res = await apiClient.post("/members", form);
      navigate(`/members/${res.data.id}`);
    } catch (e) { console.error(e); }
  };

  const handleExportCSV = async () => {
    try {
      const response = await apiClient.get("/members/export/csv", { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `membres_bhealth_${today}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { console.error("Export error:", e); }
  };

  const columns = [
    { key: "last_name", label: "Nom" },
    { key: "first_name", label: "Prénom" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Téléphone" },
    { key: "subscription_start", label: "Début abo." },
    { key: "subscription_end", label: "Fin abo." },
    { key: "price_eur", label: "Prix" },
    { key: "email_status", label: "Mail" },
    { key: "status", label: "Statut" },
  ];

  const EmailStatusBadge = ({ status }) => {
    if (status === "SENT") return <span className="inline-flex items-center gap-1 crm-badge bg-green-100 text-green-700" data-testid="email-status-sent"><MailCheck size={12} /> Envoyé</span>;
    if (status === "FAILED") return <span className="inline-flex items-center gap-1 crm-badge bg-red-100 text-red-600" data-testid="email-status-failed"><MailX size={12} /> Échec</span>;
    if (status === "NOT_REQUESTED") return <span className="inline-flex items-center gap-1 crm-badge bg-gray-100 text-gray-500" data-testid="email-status-not-requested"><Mail size={12} /> Non envoyé</span>;
    return <span className="text-[#c1c9d0]">—</span>;
  };

  return (
    <div className="crm-page">
      <div className="crm-page-header">
        <div>
          <h1 className="crm-page-title">Membres</h1>
          <p className="crm-page-subtitle">{members.length} membre{members.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button onClick={handleExportCSV} variant="outline" className="border-[#1a2744] text-[#1a2744] hover:bg-[#f4f7fa]" data-testid="export-members-csv">
              <Download size={16} className="mr-1" /> Export CSV
            </Button>
          )}
          <Button onClick={() => setShowAdd(true)} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]">
            <Plus size={16} className="mr-1" /> Nouveau membre
          </Button>
        </div>
      </div>

      <div className="crm-filters">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a1aeba]" size={16} />
          <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-10 border-[#c1c9d0]" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === "ALL" ? "" : v)}>
          <SelectTrigger className="w-[160px] h-10 border-[#c1c9d0]"><SelectValue placeholder="Tous statuts" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tous statuts</SelectItem>
            <SelectItem value="ACTIVE">Actif</SelectItem>
            <SelectItem value="SUSPENDED">Suspendu</SelectItem>
            <SelectItem value="EXPIRED">Expiré</SelectItem>
            <SelectItem value="FORMER">Inactif</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="py-8 text-center text-[#577083]">Chargement...</p>
      ) : members.length === 0 ? (
        <div className="crm-empty">
          <UserCircle size={48} className="text-[#c1c9d0] mb-3" />
          <p>Aucun membre trouvé</p>
        </div>
      ) : (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th 
                    key={col.key} 
                    onClick={() => handleSort(col.key)}
                    className="cursor-pointer select-none hover:bg-[#f4f7fa] transition-colors"
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      <SortIcon sortKey={col.key} currentSort={sortBy} currentDirection={sortDirection} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map((m) => (
                <tr key={m.id} onClick={() => navigate(`/members/${m.id}`)} className="cursor-pointer hover:bg-[#f4f7fa]">
                  <td className="font-medium">{m.last_name}</td>
                  <td>{m.first_name}</td>
                  <td className="text-[#577083]">{m.email}</td>
                  <td className="text-[#577083]">{m.phone}</td>
                  <td className="text-[#577083] text-sm">{m.subscription_start ? formatDateEU(m.subscription_start) : "—"}</td>
                  <td className="text-[#577083] text-sm">{m.subscription_end ? formatDateEU(m.subscription_end) : "—"}</td>
                  <td className="text-[#577083] text-sm font-medium">{m.price_eur ? `${m.price_eur}€` : "—"}</td>
                  <td><EmailStatusBadge status={m.email_status} /></td>
                  <td><span className={`crm-badge ${statusColors[m.status] || ""}`}>{statusLabels[m.status] || m.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau membre</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <Input placeholder="Nom *" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} />
            <Input placeholder="Prénom *" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} />
            <Input placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
            <Input placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Annuler</Button>
            <Button onClick={handleAdd} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembersPage;
